// agency.js - COMPLETE VERSION WITH PROPER CORS DETECTION (NO LOGS)
import { base_url } from './constants';
import axios from 'axios';

// ============================================
// 1. GLOBAL ERROR STATE MANAGEMENT
// ============================================
let globalErrorListeners = [];
let isApiDown = false;
let recoveryTimer = null;

export const addApiStatusListener = listener => {
  globalErrorListeners.push(listener);
  return () => {
    globalErrorListeners = globalErrorListeners.filter(l => l !== listener);
  };
};

const notifyListeners = error => {
  globalErrorListeners.forEach(listener => listener(error));
};

let corsErrorTimer = null;
let healthCheckInterval = null;
const startHealthCheck = () => {
  if (healthCheckInterval) clearInterval(healthCheckInterval);

  healthCheckInterval = setInterval(async () => {
    if (isApiDown) {
      try {
        await axios.get(`${base_url}Agency/GetProfile?loginId=test`, {
          timeout: 5000,
          validateStatus: false
        });

        isApiDown = false;
        notifyListeners(null);
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;

        if (recoveryTimer) clearTimeout(recoveryTimer);
        recoveryTimer = setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (error) {
        // Still down
      }
    }
  }, 10000);
};

// ============================================
// 2. UNIFIED AXIOS INSTANCE WITH CORS HANDLING
// ============================================
const GetAuthToken = () => {
  return sessionStorage.getItem('UserAuthToken');
};

const ApiClient = axios.create({
  baseURL: base_url,
  responseType: 'json',
  timeout: 600000
});

// Create a separate client for auth calls (no token)
const AuthApiClient = axios.create({
  baseURL: base_url,
  responseType: 'json',
  timeout: 30000
});

// Request interceptor for main ApiClient
ApiClient.interceptors.request.use(
  async config => {
    if (isApiDown) {
      const error = new Error('System is under maintenance');
      error.code = 'ERR_NETWORK';
      error.isCorsError = true;
      return Promise.reject(error);
    }

    const authToken = GetAuthToken();
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Request interceptor for AuthApiClient (NO TOKEN)
AuthApiClient.interceptors.request.use(
  async config => {
    if (isApiDown) {
      const error = new Error('System is under maintenance');
      error.code = 'ERR_NETWORK';
      error.isCorsError = true;
      return Promise.reject(error);
    }
    // NO token added here for auth calls
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for main ApiClient
ApiClient.interceptors.response.use(
  response => {
    if (corsErrorTimer) {
      clearTimeout(corsErrorTimer);
      corsErrorTimer = null;
    }
    if (isApiDown) {
      isApiDown = false;
      notifyListeners(null);
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;
      }
    }
    return response;
  },
  error => {
    let isCorsOrNetworkError = false;

    // Status 0 with response object = CORS/Network error
    if (error.response && error.response.status === 0) {
      isCorsOrNetworkError = true;
    }
    // Real HTTP error responses (401, 403, 404, 500, etc.) - NOT CORS
    else if (
      error.response &&
      error.response.status > 0 &&
      error.response.status !== 503
    ) {
      return Promise.reject(error);
    }
    // Service unavailable
    else if (error.response && error.response.status === 503) {
      isCorsOrNetworkError = true;
    }
    // Network errors without response
    else if (
      error.code === 'ERR_NETWORK' ||
      error.message === 'Network Error'
    ) {
      isCorsOrNetworkError = true;
    }
    // CORS specific errors
    else if (error.message && error.message.includes('CORS')) {
      isCorsOrNetworkError = true;
    }
    // Timeout errors
    else if (error.code === 'ECONNABORTED') {
      isCorsOrNetworkError = true;
    }

    if (isCorsOrNetworkError && !isApiDown) {
      if (!corsErrorTimer) {
        corsErrorTimer = setTimeout(() => {
          corsErrorTimer = null;
          if (!isApiDown) {
            isApiDown = true;
            const errorObj = {
              type: 'CORS_ERROR',
              message:
                'System is currently being updated. Please wait while we restore services...',
              originalError: error,
              timestamp: new Date().toISOString()
            };
            notifyListeners(errorObj);
            startHealthCheck();
          }
        }, 60000);
      }
    }

    return Promise.reject(error);
  }
);

// Response interceptor for AuthApiClient (same CORS handling but NO token errors)
AuthApiClient.interceptors.response.use(
  response => {
    if (corsErrorTimer) {
      clearTimeout(corsErrorTimer);
      corsErrorTimer = null;
    }
    if (isApiDown) {
      isApiDown = false;
      notifyListeners(null);
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
        healthCheckInterval = null;
      }
    }
    return response;
  },
  error => {
    let isCorsOrNetworkError = false;

    // Status 0 with response object = CORS/Network error
    if (error.response && error.response.status === 0) {
      isCorsOrNetworkError = true;
    }
    // Real HTTP error responses (401, 403, 404, 500, etc.) - NOT CORS
    else if (
      error.response &&
      error.response.status > 0 &&
      error.response.status !== 503
    ) {
      return Promise.reject(error);
    }
    // Service unavailable
    else if (error.response && error.response.status === 503) {
      isCorsOrNetworkError = true;
    }
    // Network errors without response
    else if (
      error.code === 'ERR_NETWORK' ||
      error.message === 'Network Error'
    ) {
      isCorsOrNetworkError = true;
    }
    // CORS specific errors
    else if (error.message && error.message.includes('CORS')) {
      isCorsOrNetworkError = true;
    }
    // Timeout errors
    else if (error.code === 'ECONNABORTED') {
      isCorsOrNetworkError = true;
    }

    if (isCorsOrNetworkError && !isApiDown) {
      if (!corsErrorTimer) {
        corsErrorTimer = setTimeout(() => {
          corsErrorTimer = null;
          if (!isApiDown) {
            isApiDown = true;
            const errorObj = {
              type: 'CORS_ERROR',
              message:
                'System is currently being updated. Please wait while we restore services...',
              originalError: error,
              timestamp: new Date().toISOString()
            };
            notifyListeners(errorObj);
            startHealthCheck();
          }
        }, 60000);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// 3. HELPER FUNCTION FOR WRAPPING API CALLS
// ============================================
const handleApiCall = async (apiCall, fallbackData = null) => {
  try {
    return await apiCall();
  } catch (error) {
    throw error;
  }
};

// ============================================
// 4. AUTHENTICATION APIs (Using AuthApiClient - NO TOKEN)
// ============================================
export const AagencyLogin = async (username, pass) => {
  return await AuthApiClient.post(`/Agency/SignIn`, {
    loginId: username,
    password: pass
  });
};

export const AagencyLogout = async () => {
  return await ApiClient.post(`/Agency/SignOut`);
};

export const AagencyForgot = async email => {
  return await AuthApiClient.post(`/Agency/Forgot`, {
    loginId: email
  });
};

// ============================================
// 5. PROFILE APIs (Using ApiClient with token)
// ============================================
export const GetAagencyUserDetails = async loginId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetProfile?loginId=${loginId}`)
  );
};

export const UpdateAgencyProfile = async UpdatedProfileObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateProfile`, UpdatedProfileObj)
  );
};

export const UploadUserProfileImage = async (agencyId, formdata) => {
  return await handleApiCall(() =>
    ApiClient.post(
      `/Agency/UploadUserProfileImage?loginId=${agencyId}`,
      formdata
    )
  );
};

export const AgencyAdminGetProfile = async AgencyLoginID => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetProfile?loginId=${AgencyLoginID}`)
  );
};

// ============================================
// 6. USER MANAGEMENT APIs
// ============================================
export const GetUsers = async agencyId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetUsers?agencyId=${agencyId}`)
  );
};

export const GetUserRoles = async agencyId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetUserRoles?agencyId=${agencyId}`)
  );
};

export const CreateAgencyUser = async userObj => {
  return await handleApiCall(() => ApiClient.post(`/Agency/SignUp`, userObj));
};

export const CreateAgencyRole = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/CreateAgencyRole`, obj)
  );
};

export const GetRolePermissions = async roleId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetRolePermissions?roleId=${roleId}`)
  );
};

export const ResendPassword = async loginId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/ResendPassword?loginId=${loginId}`)
  );
};

// ============================================
// 7. PASSWORD MANAGEMENT APIs
// ============================================
export const UpdateAgencyPassword = async (
  newPass,
  currentPass,
  UserLoggedInEmail
) => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpadatePassword`, {
      loginId: UserLoggedInEmail,
      passwordKey: '',
      password: newPass,
      currentPassword: currentPass
    })
  );
};

export const UpadateForgotPassword = async (
  confirmPassword,
  ForgotPassKey,
  LoginEmail
) => {
  return await handleApiCall(() =>
    AuthApiClient.post(`/Agency/UpadateForgotPassword`, {
      loginId: LoginEmail,
      passwordKey: ForgotPassKey,
      password: confirmPassword,
      currentPassword: ''
    })
  );
};

// ============================================
// 8. SHIPMENT APIs
// ============================================
export const GetShipments = async (
  agencyId,
  loggedInEmail,
  pageNum,
  pageSize,
  searchQuery,
  shipmentStatuses,
  datesRange
) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Shipment/GetShipments?agencyId=${agencyId}&loginId=${loggedInEmail}&pageNumber=${pageNum}&pageSize=${pageSize}&searchQuery=${searchQuery}&shipmentStatuses=${shipmentStatuses}&fromDate=${datesRange.from}&toDate=${datesRange.to}`
    )
  );
};

export const GetBookedShipments = async agencyId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/GetBookedShipments?agencyId=${agencyId}`)
  );
};

export const GetShipmentDetails = async shipmentId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/GetShipmentDetails?shipmentId=${shipmentId}`)
  );
};

export const UpdateQuoteCarrier = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UpdateQuoteCarrier`, obj)
  );
};

export const UpdateShipment = async shipObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UpdateShipment`, shipObj)
  );
};

export const CreateShipment = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/CreateShipment`, obj)
  );
};

export const DispatchShipment = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/DispatchShipment`, obj)
  );
};

export const HoldShipment = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/HoldShipment`, obj)
  );
};

export const UnbookShipment = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UnbookShipment`, obj)
  );
};

export const CancelShipment = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/CancelShipment`, obj)
  );
};

export const CancelTLShipment = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/CancelTLShipment`, obj)
  );
};

export const UnHoldShipment = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UnHoldShipment`, obj)
  );
};

export const ClaimShipment = async obj => {
  return await handleApiCall(() => ApiClient.post(`/Shipment/Claim`, obj));
};

export const GetShipmentClaims = async obj => {
  return await handleApiCall(() => ApiClient.get(`/Shipment/Claims`));
};

export const GetClaimedShipmentDetails = async shipmentId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/Claim?shipmentId=${shipmentId}`)
  );
};

export const ImportOrder = async (agencyId, formdata) => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/ImportOrder?agencyId=${agencyId}`, formdata)
  );
};

export const ImportInvoiceAuditShipments = async (carrierId, CsvArray) => {
  return await handleApiCall(() =>
    ApiClient.post(
      `/Shipment/ImportInvoiceAuditShipments?carrierId=${carrierId}`,
      CsvArray
    )
  );
};

export const GetBillOfLading = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Carriers/BillOfLading`, obj)
  );
};

export const GetProofOfDelivery = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Carriers/ProofOfDelivery`, obj)
  );
};

export const PickupShipment = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Carriers/PickupShipment`, obj)
  );
};

export const StartTLQuote = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/StartTLQuote`, obj)
  );
};

export const GetQuote = async quoteId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/GetQuote?quoteId=${quoteId}`)
  );
};

export const GetQuoteDetail = async Id => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/GetQuoteDetail?Id=${Id}`)
  );
};

export const BookTLShipment = async bookTLShipmentObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/BookTLShipment`, bookTLShipmentObj)
  );
};

export const UpdateTLShipment = async UpdateTLShipmentObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UpdateTLShipment`, UpdateTLShipmentObj)
  );
};

export const ResendQuote = async Id => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/ResendQuote?Id=${Id}`)
  );
};

export const AddMoreRateRequest = async Id => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/AddMoreRate?Id=${Id}`)
  );
};

export const GetShipmentActivity = async shipmentId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/GetShipmentActivity?shipmentId=${shipmentId}`)
  );
};

export const InvoiceAuditLogs = async shipmentId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/InvoiceAuditLogs?shipmentId=${shipmentId}`)
  );
};

export const UpdateShipmentContacts = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UpdateShipmentContacts`, obj)
  );
};

export const UpdateShipmentDetail = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UpdateShipmentDetail`, obj)
  );
};

export const SendDisputedInvoiceReport = async InvoiceReportObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/SendDisputedInvoiceReport`, InvoiceReportObj)
  );
};

export const UpdateShipmentLocation = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UpdateShipmentLocation`, obj)
  );
};

export const CheckLocation = async shipmentId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/CheckLocation?shipmentId=${shipmentId}`)
  );
};

export const GetNextDate = async (startDate, days, dateFormat) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Shipment/GetNextDate?startDate=${startDate}&days=${days}&outputFormat=${dateFormat}`
    )
  );
};

export const GetShipmentById = async shipmentId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/GetShipmentById?shipmentId=${shipmentId}`)
  );
};

// ============================================
// 9. TRACKING APIs
// ============================================
export const GetTrackShipment = async reffNO => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/GetShipment?orderNumber=${reffNO}`)
  );
};

export const TrackShipmentRequest = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Carriers/TrackShipment`, obj)
  );
};

export const TLShipmentHistory = async (carrierId, fromZip, toZip) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Shipment/TLShipmentHistory?carrierId=${carrierId}&fromZip=${fromZip}&toZip=${toZip}`
    )
  );
};

export const CheckTrackingStatus = async () => {
  return await ApiClient.get(`/Jobs/CheckTrackingStatus`);
};

// ============================================
// 10. CARRIER APIs
// ============================================
export const GetCarriers = async (agencyId, locationId) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Agency/GetAllCarriers?agencyId=${agencyId}&locationId=${locationId ? locationId : 0}`
    )
  );
};

export const GetActiveCarriers = async (agencyId, locationId, signal) => {
  return ApiClient.get(
    `/Agency/GetActiveCarriers?agencyId=${agencyId}&locationId=${locationId ? locationId : 0}`,
    { signal }
  );
};

export const GetCarrierDetails = async (agencyId, carrierId, locationID) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Agency/GetCarrierDetails?agencyId=${agencyId}&carrierId=${carrierId}&locationId=${locationID}`
    )
  );
};

export const UpdateCarrierDetails = async carriersArray => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateCarrierDetails`, carriersArray)
  );
};

export const UpdateCarrierBillTo = async data => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateCarrierBillTo`, data)
  );
};

export const UpdateCarrierClaimSettings = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateCarrierClaimSettings`, obj)
  );
};

export const UpdateCarrierRateQuoteSettings = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateCarrierRateQuoteSettings`, obj)
  );
};

export const UpdateCarrierSurcharge = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateCarrierSurcharge`, obj)
  );
};

export const UpdateCarrierProSeries = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateCarrierProSeries`, obj)
  );
};

export const UpdateCarrierThirdPartyInfo = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateCarrierThirdPartyInfo`, obj)
  );
};

export const CreateOrUpdateCarrier = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/CreateOrUpdateCarrier`, obj)
  );
};

export const GetCarrierEProSeries = async carrierId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/CarrierEProSeries?carrierId=${carrierId}`)
  );
};

export const GenerateCarrierEProSeries = async dataObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Carriers/GenerateProSeries`, dataObj)
  );
};

export const ImportCarrierEProSeries = async dataObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/ImportCarrierEProSeries`, dataObj)
  );
};

export const GetNextProNumber = async (agencyID, carrierId) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Carriers/GetNextProNumber?agencyId=${agencyID}&carrierId=${carrierId}`
    )
  );
};

export const UpdateCarrierInvoiceAuditThreshold = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateCarrierInvoiceAuditThreshold`, obj)
  );
};

export const GetCarrierImportCol = async carrierId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetCarrier?carrierId=${carrierId}`)
  );
};

export const GetCarrierProSeriesStatus = async (agencyId, locationId) => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetCarrierProSeriesStats`)
  );
};

export const MarkAsResolvedDisputedShipment = async shipId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/MarkAsResolvedDisputedShipment?id=${shipId}`)
  );
};

export const GetCarrierLocations = async (agencyId, carrierId) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Agency/GetCarrierLocations?agencyId=${agencyId}&carrierId=${carrierId}`
    )
  );
};

export const UploadCarrierLogo = async (agencyId, formData) => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UploadCarrierLogo?agencyId=${agencyId}`, formData)
  );
};

// ============================================
// 11. LOCATION APIs
// ============================================
export const GetLocations = async agencyId => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetLocations?agencyId=${agencyId}`)
  );
};

export const GetLocationCarriers = async (agencyID, locationID) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Agency/GetLocationCarriers?agencyId=${agencyID}&locationId=${locationID}`
    )
  );
};

export const AddUpdateLocation = async locationObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/AddUpdateLocation`, locationObj)
  );
};

export const AddUpdateLocationCarrier = async locationCarrierObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/AddUpdateLocationCarrier`, locationCarrierObj)
  );
};

export const GetAgencyLocations = async (agencyID, locationId) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Agency/GetAgencyLocations?agencyId=${agencyID}&locationId=${locationId}`
    )
  );
};

export const SetAsDefaultLocation = async locationId => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/SetAsDefaultLocation?locationId=${locationId}`)
  );
};

export const UpdateLocationCarrier = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateLocationCarrier`, obj)
  );
};

export const GetLocationEProSeries = async (locationId, carrierId) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Agency/CarrierEProSeries?carrierId=${carrierId}&locationId=${locationId}`
    )
  );
};

// ============================================
// 12. NOTIFICATION APIs
// ============================================
export const GetAppNotifications = async (unread, records) => {
  return await handleApiCall(() =>
    ApiClient.get(
      `/Agency/AppNotifications?unread=${unread}&records=${records}`
    )
  );
};

export const AppNotificationMarkAsRead = async nid => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/AppNotificationMarkAsRead?notificationId=${nid}`)
  );
};

export const SaveNotificationSettings = async data => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/SaveNotificationSettings`, data)
  );
};

export const GetNotificationSettings = async () => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/NotificationSettings`)
  );
};

export const MarkAsReadMessages = async idsArray => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/MarkAsReadMessages`, idsArray)
  );
};

// ============================================
// 13. SETTINGS APIs
// ============================================
export const GetGeneralSettings = async () => {
  return await handleApiCall(() => ApiClient.get(`/Agency/GetSettings`));
};

export const UpdateGeneralSettings = async data => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateSettings`, data)
  );
};

export const SaveUserSettings = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/SaveUserSettings`, obj)
  );
};

// ============================================
// 14. FILE UPLOAD APIs
// ============================================
export const UploadFileAPI = async (destination, file) => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/UploadFile?destination=${destination}`, file)
  );
};

export const GetLogoBase64 = async filePath => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetLogoBase64?filePath=${filePath}`)
  );
};

// ============================================
// 15. IMPORT/EXPORT APIs
// ============================================
export const GetImportFieldNames = async UpdatedProfileObj => {
  return await handleApiCall(() =>
    ApiClient.get(`/Shipment/GetImportFieldNames`, { UpdatedProfileObj })
  );
};

export const ImportCSVMapping = async (
  CsvArray,
  AgencyID,
  UserLoggedInEmail
) => {
  return await handleApiCall(() =>
    ApiClient.post(
      `/Shipment/ImportCSVMapping?agencyId=${AgencyID}&loginId=${UserLoggedInEmail}`,
      CsvArray
    )
  );
};

export const UpdateIAColumnMapping = async obj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/UpdateIAColumnMapping`, obj)
  );
};

// ============================================
// 16. APP REGISTRATION APIs
// ============================================
export const SaveAppRegistration = async appRegistrationObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Agency/SaveAppRegistration`, appRegistrationObj)
  );
};

export const GetAppRegistrations = async agencyID => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/GetAppRegistrations?agencyId=${agencyID}`)
  );
};

// ============================================
// 17. CHAT/LOG APIs
// ============================================
export const LogTLCarrierResponse = async chatObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/LogTLCarrierResponse`, chatObj)
  );
};

export const LogTLCarrierReScheduleResponse = async rescheduleObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/LogTLCarrierReScheduleResponse`, rescheduleObj)
  );
};

export const LogTLCarrierReQuoteResponse = async chatObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/LogTLCarrierReQuoteResponse`, chatObj)
  );
};

export const LogTLShipperResponse = async chatObj => {
  return await handleApiCall(() =>
    ApiClient.post(`/Shipment/LogTLShipperResponse`, chatObj)
  );
};

export const FailedLogs = async obj => {
  return await handleApiCall(() => ApiClient.get(`/Agency/FailedLogs`, obj));
};

export const MarkAsResolvedLog = async id => {
  return await handleApiCall(() =>
    ApiClient.get(`/Agency/MarkAsResolvedLog?id=${id}`)
  );
};

// ============================================
// 18. GOOGLE/MAP APIs
// ============================================
export const GoogleMapApi = async squery => {
  return await ApiClient.get(
    `https://google-autocomplete-api.onrender.com/api/GoogleMapApi?input=${squery}`
  );
};

export const GoogleAddressDetailsApi = async firstPlaceId => {
  return await ApiClient.get(
    `https://google-autocomplete-api.onrender.com/api/GoogleAddressDetailsApi/?place_id=${firstPlaceId}`
  );
};

export const GetLocationsJSON = async squery => {
  return await ApiClient.get(`/Carriers/SearchLocation?query=${squery}`);
};

export const AddLocation = async obj => {
  return await ApiClient.post(`/Carriers/AddLocation`, obj);
};

export const CalculateDistance = async (from, to) => {
  return await ApiClient.get(
    `/Carriers/CalculateDistance?from=${from}&to=${to}`
  );
};

// ============================================
// 19. DASHBOARD APIs
// ============================================
export const EmbedDashboard = async () => {
  return await ApiClient.get(`/Dashboard/EmbedDashboard`);
};

// ============================================
// 20. ADMIN APIs
// ============================================
export const GetAllCarriers = async type => {
  return await handleApiCall(() =>
    ApiClient.get(`/Admin/GetCarriers?carrierType=${type}`)
  );
};

// ============================================
// 21. APPS APIs
// ============================================
export const Authenticate = async obj => {
  return await ApiClient.post(`/Apps/Authenticate`, obj);
};

export const QuoteAndSchedule = async (obj, token) => {
  return await ApiClient.post(`/Apps/QuoteAndSchedule`, obj, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

export const Pickup = async (obj, token) => {
  return await ApiClient.post(`/Apps/Pickup`, obj, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

// ============================================
// 22. EXPORT UTILITIES FOR COMPONENTS
// ============================================
export const isApiCurrentlyDown = () => isApiDown;

export const triggerCorsError = () => {
  if (!isApiDown) {
    isApiDown = true;
    notifyListeners({
      type: 'CORS_ERROR',
      message:
        'System is currently being updated. Please wait while we restore services...',
      timestamp: new Date().toISOString()
    });
    startHealthCheck();
  }
};
export const forceHealthCheck = () => startHealthCheck();
